/**
 * useCurrencyUIState Hook
 * 
 * Consolidates all UI state management for CurrencySettings page.
 */

import { useState } from "react";
import type React from "react";
import { Currency } from "../../../types";

export interface UseCurrencyUIStateReturn {
  // Dialog states
  addDialogOpen: boolean;
  setAddDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editDialogOpen: boolean;
  setEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  defaultCurrencyDialogOpen: boolean;
  setDefaultCurrencyDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refreshDialogOpen: boolean;
  setRefreshDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editDecimalPlacesDialogOpen: boolean;
  setEditDecimalPlacesDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Currency editing states
  editingCurrency: Currency | null;
  setEditingCurrency: React.Dispatch<React.SetStateAction<Currency | null>>;
  newCurrencyCode: { code: string; name: string; symbol: string } | null;
  setNewCurrencyCode: React.Dispatch<React.SetStateAction<{ code: string; name: string; symbol: string } | null>>;
  editCurrencyRate: string;
  setEditCurrencyRate: React.Dispatch<React.SetStateAction<string>>;
  selectedDefaultCurrency: string;
  setSelectedDefaultCurrency: React.Dispatch<React.SetStateAction<string>>;
  currencyToDelete: string;
  setCurrencyToDelete: React.Dispatch<React.SetStateAction<string>>;
  currencyToRefresh: Currency | null;
  setCurrencyToRefresh: React.Dispatch<React.SetStateAction<Currency | null>>;
  decimalPlaces: number;
  setDecimalPlaces: React.Dispatch<React.SetStateAction<number>>;
  editingDecimalPlacesCurrency: Currency | null;
  setEditingDecimalPlacesCurrency: React.Dispatch<React.SetStateAction<Currency | null>>;
  updatingDecimalPlaces: boolean;
  setUpdatingDecimalPlaces: React.Dispatch<React.SetStateAction<boolean>>;

  // Loading states
  addingCurrency: boolean;
  setAddingCurrency: React.Dispatch<React.SetStateAction<boolean>>;
  refreshingCurrency: boolean;
  setRefreshingCurrency: React.Dispatch<React.SetStateAction<boolean>>;
  fetchingRateFromAPI: boolean;
  setFetchingRateFromAPI: React.Dispatch<React.SetStateAction<boolean>>;
  fetchedRateFromAPI: number | null;
  setFetchedRateFromAPI: React.Dispatch<React.SetStateAction<number | null>>;

  // Snackbar states
  snackbarOpen: boolean;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  snackbarMessage: string;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  snackbarElement: React.ReactElement | null;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
}

/**
 * Hook for managing all UI state in CurrencySettings
 */
export const useCurrencyUIState = (): UseCurrencyUIStateReturn => {
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [defaultCurrencyDialogOpen, setDefaultCurrencyDialogOpen] = useState<boolean>(false);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState<boolean>(false);
  const [editDecimalPlacesDialogOpen, setEditDecimalPlacesDialogOpen] = useState<boolean>(false);

  // Currency editing states
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [newCurrencyCode, setNewCurrencyCode] = useState<{ code: string; name: string; symbol: string } | null>(null);
  const [editCurrencyRate, setEditCurrencyRate] = useState<string>("1.0");
  const [selectedDefaultCurrency, setSelectedDefaultCurrency] = useState<string>("");
  const [currencyToDelete, setCurrencyToDelete] = useState<string>("");
  const [currencyToRefresh, setCurrencyToRefresh] = useState<Currency | null>(null);
  const [decimalPlaces, setDecimalPlaces] = useState<number>(2); // Default to 2 decimal places
  const [editingDecimalPlacesCurrency, setEditingDecimalPlacesCurrency] = useState<Currency | null>(null);
  const [updatingDecimalPlaces, setUpdatingDecimalPlaces] = useState<boolean>(false);

  // Loading states
  const [addingCurrency, setAddingCurrency] = useState<boolean>(false);
  const [refreshingCurrency, setRefreshingCurrency] = useState<boolean>(false);
  const [fetchingRateFromAPI, setFetchingRateFromAPI] = useState<boolean>(false);
  const [fetchedRateFromAPI, setFetchedRateFromAPI] = useState<number | null>(null);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarElement, setSnackbarElement] = useState<React.ReactElement | null>(null);

  return {
    // Dialog states
    addDialogOpen,
    setAddDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    defaultCurrencyDialogOpen,
    setDefaultCurrencyDialogOpen,
    refreshDialogOpen,
    setRefreshDialogOpen,
    editDecimalPlacesDialogOpen,
    setEditDecimalPlacesDialogOpen,

    // Currency editing states
    editingCurrency,
    setEditingCurrency,
    newCurrencyCode,
    setNewCurrencyCode,
    editCurrencyRate,
    setEditCurrencyRate,
    selectedDefaultCurrency,
    setSelectedDefaultCurrency,
    currencyToDelete,
    setCurrencyToDelete,
    currencyToRefresh,
    setCurrencyToRefresh,
    decimalPlaces,
    setDecimalPlaces,
    editingDecimalPlacesCurrency,
    setEditingDecimalPlacesCurrency,
    updatingDecimalPlaces,
    setUpdatingDecimalPlaces,

    // Loading states
    addingCurrency,
    setAddingCurrency,
    refreshingCurrency,
    setRefreshingCurrency,
    fetchingRateFromAPI,
    setFetchingRateFromAPI,
    fetchedRateFromAPI,
    setFetchedRateFromAPI,

    // Snackbar states
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    setSnackbarMessage,
    snackbarElement,
    setSnackbarElement,
  };
};
